import { createContext, type ReactNode, useContext, useMemo } from "react";

export type ProductViewerLoadingScreen = Exclude<ReactNode, boolean> | false;
export type CameraAutoFitTrigger = "configChange" | "canvasResize";
export type CameraAutoFit = false | readonly CameraAutoFitTrigger[];

export enum CameraControlsMode {
  FixedOrbit = "fixed-orbit",
  FreeOrbit = "free-orbit",
  None = "none",
}

export const DEFAULT_CAMERA_CONTROLS_MODE = CameraControlsMode.FixedOrbit;

export interface ProductViewerConfig {
  loadingScreen?: ProductViewerLoadingScreen;
  cameraControls?: CameraControlsMode;
  cameraAutoFit?: CameraAutoFit;
}

/** Default when no `ProductViewerConfigProvider` is used (emerge direct consumers). */
const EMERGE_CONTEXT_DEFAULT_CAMERA_CONTROLS = CameraControlsMode.FreeOrbit;

const ProductViewerConfigContext = createContext<ProductViewerConfig>({
  cameraControls: EMERGE_CONTEXT_DEFAULT_CAMERA_CONTROLS,
});

export const ProductViewerConfigProvider = ({
  children,
  loadingScreen,
  cameraControls = DEFAULT_CAMERA_CONTROLS_MODE,
  cameraAutoFit = false,
}: ProductViewerConfig & {
  children: ReactNode;
}) => {
  const value = useMemo<ProductViewerConfig>(
    () => ({ loadingScreen, cameraControls, cameraAutoFit }),
    [loadingScreen, cameraControls, cameraAutoFit],
  );

  return (
    <ProductViewerConfigContext.Provider value={value}>
      {children}
    </ProductViewerConfigContext.Provider>
  );
};

export const useProductViewerConfig = () =>
  useContext(ProductViewerConfigContext);
